/**
 * Legacy URL Slug Mapping (French/Italian)
 *
 * Maps German URL slugs to their retired French and Italian equivalents.
 * The FR/IT site versions have been removed; this map is kept solely as the
 * legacy source for `redirects.ts`, which translates incoming /fr/* and /it/*
 * URLs back to their German canonical path (301).
 *
 * This is NOT the source of truth for active URL localization — the live site
 * is German + English, configured in the website package's url-slug-mapping.ts.
 * Do not add new languages here.
 */

export const SUPPORTED_LANGUAGES = ["de", "fr", "it"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Mapping of German slugs to localized slugs for each language
export type SlugMapping = Record<string, Record<SupportedLanguage, string>>;

/**
 * Category slugs (top-level paths)
 * These are the main sections of the website
 */
export const categorySlugMapping: SlugMapping = {
  kunden: {
    de: "kunden",
    fr: "clients",
    it: "clienti",
  },
  "ueber-uns": {
    de: "ueber-uns",
    fr: "a-propos",
    it: "chi-siamo",
  },
  produkte: {
    de: "produkte",
    fr: "produits",
    it: "prodotti",
  },
  loesungen: {
    de: "loesungen",
    fr: "solutions",
    it: "soluzioni",
  },
  dienstleistungen: {
    de: "dienstleistungen",
    fr: "services",
    it: "servizi",
  },
  module: {
    de: "module",
    fr: "modules",
    it: "moduli",
  },
  agb: {
    de: "agb",
    fr: "cgv",
    it: "cgc",
  },
  datenschutz: {
    de: "datenschutz",
    fr: "confidentialite",
    it: "privacy",
  },
  partner: {
    de: "partner",
    fr: "partenaires",
    it: "partner",
  },
  jobs: {
    de: "jobs",
    fr: "emplois",
    it: "lavoro",
  },
  blog: {
    de: "blog",
    fr: "blog",
    it: "blog",
  },
  branchen: {
    de: "branchen",
    fr: "secteurs",
    it: "settori",
  },
  branche: {
    de: "branche",
    fr: "secteur",
    it: "settore",
  },
  plattform: {
    de: "plattform",
    fr: "plateforme",
    it: "piattaforma",
  },
  platform: {
    de: "platform",
    fr: "plateforme",
    it: "piattaforma",
  },
  "success-stories": {
    de: "success-stories",
    fr: "success-stories",
    it: "success-stories",
  },
};

/**
 * Page-specific slugs (sub-paths)
 * These are specific pages within the main categories
 */
export const pageSlugMapping: SlugMapping = {
  // Solution pages
  "vertriebs-und-auftragsverwaltung": {
    de: "vertriebs-und-auftragsverwaltung",
    fr: "vente-et-gestion-commandes",
    it: "vendita-e-gestione-ordini",
  },
  "digitaler-kundendienst": {
    de: "digitaler-kundendienst",
    fr: "service-client-numerique",
    it: "servizio-clienti-digitale",
  },
  "custom-cds-portal": {
    de: "custom-cds-portal",
    fr: "portail-cds-personnalise",
    it: "portale-cds-personalizzato",
  },

  // Service pages
  "fachliche-individualisierung": {
    de: "fachliche-individualisierung",
    fr: "personnalisation-fonctionnelle",
    it: "personalizzazione-funzionale",
  },
  "integration-consulting": {
    de: "integration-consulting",
    fr: "conseil-integration",
    it: "consulenza-integrazione",
  },

  // Product pages
  "vertriebsportal-starter": {
    de: "vertriebsportal-starter",
    fr: "portail-de-vente-starter",
    it: "portale-di-vendita-starter",
  },
  "vertriebsportal-enterprise": {
    de: "vertriebsportal-enterprise",
    fr: "portail-de-vente-enterprise",
    it: "portale-di-vendita-enterprise",
  },
  "vertriebsportal-enterprise-hubspot": {
    de: "vertriebsportal-enterprise-hubspot",
    fr: "portail-de-vente-enterprise-hubspot",
    it: "portale-di-vendita-enterprise-hubspot",
  },

  // VP sub-pages
  features: {
    de: "features",
    fr: "fonctionnalites",
    it: "funzionalita",
  },
  win: {
    de: "win",
    fr: "gagner",
    it: "vinci",
  },

  // Module pages
  cpq: {
    de: "cpq",
    fr: "cpq",
    it: "cpq",
  },
  appointments: {
    de: "appointments",
    fr: "appointments",
    it: "appointments",
  },
  messaging: {
    de: "messaging",
    fr: "messaging",
    it: "messaging",
  },
  rechnungsstellung: {
    de: "rechnungsstellung",
    fr: "facturation",
    it: "fatturazione",
  },
  iam: {
    de: "iam",
    fr: "iam",
    it: "iam",
  },
  "profile-management": {
    de: "profile-management",
    fr: "profile-management",
    it: "profile-management",
  },
  analytics: {
    de: "analytics",
    fr: "analytics",
    it: "analytics",
  },

  // Other pages
  technologie: {
    de: "technologie",
    fr: "technologie",
    it: "tecnologia",
  },
  produkt: {
    de: "produkt",
    fr: "produit",
    it: "prodotto",
  },
};

/**
 * Get the localized slug for a given German slug and language
 * @param germanSlug The German slug (e.g., "kunden")
 * @param locale The target locale ("de", "fr", or "it")
 * @returns The localized slug, or the original if no mapping exists
 */
export const getLocalizedSlug = (
  germanSlug: string,
  locale: SupportedLanguage,
): string => {
  // Try category mapping first
  const categoryMapping = categorySlugMapping[germanSlug];
  if (categoryMapping) {
    return categoryMapping[locale] || germanSlug;
  }

  // Try page mapping
  const pageMapping = pageSlugMapping[germanSlug];
  if (pageMapping) {
    return pageMapping[locale] || germanSlug;
  }

  // If no mapping found, return the original slug
  return germanSlug;
};

/**
 * Get the German slug from a localized slug
 * @param localizedSlug The localized slug (e.g., "clients")
 * @param locale The source locale ("de", "fr", or "it")
 * @returns The German slug, or the original if no mapping exists
 */
export const getGermanSlug = (
  localizedSlug: string,
  locale: SupportedLanguage,
): string => {
  // Try category mapping
  for (const [germanSlug, mappings] of Object.entries(categorySlugMapping)) {
    if (mappings[locale] === localizedSlug) {
      return germanSlug;
    }
  }

  // Try page mapping
  for (const [germanSlug, mappings] of Object.entries(pageSlugMapping)) {
    if (mappings[locale] === localizedSlug) {
      return germanSlug;
    }
  }

  // If no mapping found, assume it's already a German slug
  return localizedSlug;
};

/**
 * Translate a full URL path from one language to another
 * @param url The URL path (e.g., "/produkte/vertriebsportal-starter")
 * @param sourceLocale The source language
 * @param targetLocale The target language
 * @returns The translated URL path
 */
export const translateUrl = (
  url: string,
  sourceLocale: SupportedLanguage,
  targetLocale: SupportedLanguage,
): string => {
  if (sourceLocale === targetLocale) {
    return url;
  }

  // Remove language prefix if present
  const cleanUrl = url.replace(/^\/(de|fr|it)/, "");

  // Split the URL into segments
  const segments = cleanUrl.split("/").filter(Boolean);

  // Translate each segment
  const translatedSegments = segments.map((segment) => {
    // First convert to German (canonical form)
    const germanSlug =
      sourceLocale === "de" ? segment : getGermanSlug(segment, sourceLocale);

    // Then translate to target language
    return getLocalizedSlug(germanSlug, targetLocale);
  });

  // Reconstruct the URL
  const translatedPath = "/" + translatedSegments.join("/");

  // Add language prefix for non-German languages
  if (targetLocale === "de") {
    return translatedPath;
  } else {
    return `/${targetLocale}${translatedPath}`;
  }
};

/**
 * Get the localized URL for a given German URL and language
 * @param germanUrl The German URL (e.g., "/kunden" or "/produkte/vertriebsportal-starter")
 * @param locale The target locale ("de", "fr", or "it")
 * @returns The localized URL with language prefix if needed
 */
export const getLocalizedUrl = (
  germanUrl: string,
  locale: SupportedLanguage,
): string => {
  return translateUrl(germanUrl, "de", locale);
};

/**
 * Get the German URL from a localized URL
 * @param localizedUrl The localized URL (e.g., "/fr/clients" or "/clients")
 * @param sourceLocale The source locale ("de", "fr", or "it")
 * @returns The German canonical URL without language prefix
 */
export const getGermanUrl = (
  localizedUrl: string,
  sourceLocale: SupportedLanguage,
): string => {
  return translateUrl(localizedUrl, sourceLocale, "de");
};

/**
 * Get all German category slugs
 * @returns Array of German category slugs
 */
export const getAllGermanCategorySlugs = (): string[] => {
  return Object.keys(categorySlugMapping);
};

/**
 * Get all German page slugs
 * @returns Array of German page slugs
 */
export const getAllGermanPageSlugs = (): string[] => {
  return Object.keys(pageSlugMapping);
};

/**
 * Get all localized category slugs for a specific language
 * @param locale The target locale ("de", "fr", or "it")
 * @returns Array of localized category slugs
 */
export const getAllLocalizedCategorySlugs = (
  locale: SupportedLanguage,
): string[] => {
  return Object.values(categorySlugMapping).map(
    (mapping) => mapping[locale] || mapping.de,
  );
};

/**
 * Get all localized page slugs for a specific language
 * @param locale The target locale ("de", "fr", or "it")
 * @returns Array of localized page slugs
 */
export const getAllLocalizedPageSlugs = (
  locale: SupportedLanguage,
): string[] => {
  return Object.values(pageSlugMapping).map(
    (mapping) => mapping[locale] || mapping.de,
  );
};
